import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

/** Common Components */
import MolGrid from "../../../../components/molGrid/MolGrid";

/** Common Services & Data Files */
import SwalAlert from "../../../../services/swalService/SwalService";

/** Configuration Files */
import { clientsGridConfig } from "./clientList.grid.config";
import { useDeleteClientMutation, useGetClientListMutation, useUpdateClientMutation, useUpdateClientToggleMutation } from "../../../../app/services/ClientAPI";
import ToastService from "../../../../services/toastService/ToastService";
import PropTypes from "prop-types";

const ClientList = (props, ref) => {
  const molGridRef = useRef();

  const [dataSource, setDataSource] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortState, setSortState] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 100
  })

  const { confirm } = SwalAlert();

  const [getClient, {
    isLoading: isClientListLoading,
    isSuccess: isClientListSuccess,
    data: clientListData
  }] = useGetClientListMutation();

  const [deleteClient, {
    isLoading: isClientDeleting,
    isSuccess: isClientDeleted,
    data: clientDeleteData
  }] = useDeleteClientMutation();

  const [updateClient, {
    isLoading: isClientToggleUpdating,
    isSuccess: isClientToggleUpdated,
    data: clientToggleUpdatedData,
  }] = useUpdateClientToggleMutation();

  const getClientList = (pageObject,field) => {
    const req = {
      pagination: {
        pageNumber: pageObject.pageNumber,
        pageSize: pageObject.pageSize
      },
      filters: {
        searchText: "",
      },
      sortString: field ? field : "",
      SearchClientName: props?.search,
      status: props.status.value
    }
    getClient(req)
  };

  const handlePageChange = (pageObject) => {
    setPagination({
      pageNumber: pageObject?.pageNumber,
      pageSize: pageObject?.pageSize
    })
    getClientList(pageObject);
  };

  const sortData = (data, sortState) => {

    return [...data].sort((a, b) => {
      for (const sort of sortState) {
        const valA = a[sort.column] ?? '';
        const valB = b[sort.column] ?? '';
        const comparison = sort.direction === 'asc'
          ? valA.toString().localeCompare(valB.toString())
          : valB.toString().localeCompare(valA.toString());
        if (comparison !== 0) return comparison;
      }
      return 0;
    });
  };

  const handleSortingChange = async (sortString, col) => {
    if (!sortString) return;
    const newSortState = sortString
      .split(',')
      .map(item => {
        const [column, directionRaw] = item.trim().split(/\s+/);
        const direction = directionRaw?.toLowerCase() === 'desc' ? 'desc' : 'asc';
        return { column, direction };
      })
      .filter(item => item.column);
    const filtered = newSortState.filter(item => item.column === col.fieldName);
    setSortState(newSortState);
    let req = `${filtered[0].column} ${filtered[0].direction}`
    await getClientList(pagination, req);
    const orderData = await sortData(dataSource, filtered);
    // setDataSource(orderData);
  };


  const handleEditClick = (data) => {
    props.onEdit?.(data)
  };

  const handleViewClick = (data) => {
    props.onView?.(data);
  };

  const handleDeleteClick = (data) => {
    const clientID = data.clientID;
    confirm(
      "Delete?",
      "Are you sure you want to delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        if (clientID) {
        }
      }
    });
  };

  useEffect(() => {
    if (isClientToggleUpdated && clientToggleUpdatedData) {
      if (clientToggleUpdatedData.errorMessage) {
        ToastService.success(clientToggleUpdatedData.errorMessage);
      };
      if (molGridRef.current) {
        getClientList(molGridRef.current.getDefaultPageObject());
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientToggleUpdated, clientToggleUpdatedData]);

  const handleToggleChange = (fieldName, newRowData, rowIndex) => {
    const req = {
      clientID: newRowData.clientID,
      status: newRowData.status === true ? 1 : 0
    }
    updateClient(req);
  }

  useEffect(() => {
    if (molGridRef.current) {
      getClientList(molGridRef.current.getDefaultPageObject());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.search, props.status, props.includeInactive]);

  useEffect(() => {
    if (isClientListSuccess && clientListData) {
      if (clientListData.dataSource) {
        setDataSource(clientListData.dataSource)
      }
      if (clientListData.totalRecord) {
        setTotalCount(clientListData.totalRecord)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientListSuccess, clientListData]);

  useEffect(() => {
    if (isClientDeleted && clientDeleteData) {
      if (clientDeleteData.errorMessage) {
        ToastService.success(clientDeleteData.errorMessage);
        setDataSource([])
      };
      if (molGridRef.current) {
        getClientList(molGridRef.current.getDefaultPageObject());
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientDeleted, clientDeleteData]);

  const actionHandler = {
    VIEW: handleViewClick,
    DELETE: handleDeleteClick,
    EDIT: handleEditClick

  };

  useImperativeHandle(
    ref,
    () => ({
      refreshGrid: () => {
        if (ref.current) {
          getClientList(molGridRef.current.getDefaultPageObject());
        } else {
          return null;
        }
      },
    }),
    [ref]
  );

  return (
    <>
      <MolGrid
        ref={molGridRef}
        configuration={clientsGridConfig}
        dataSource={dataSource}
        allowPagination={true}
        pagination={{
          totalCount: totalCount,
          pageSize: 100,
          currentPage: 1,
        }}
        onActionChange={actionHandler}
        onPageChange={handlePageChange}
        isLoading={isClientListLoading || isClientToggleUpdating}
        onSorting={handleSortingChange}
        onColumnChange={handleToggleChange}
        handleCheckboxChange={props.handleCheckboxChange}
        rowDoubleClick={handleViewClick}
      />
    </>
  );
};

ClientList.propTypes = {
  search: PropTypes.string,
  status: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  includeInactive: PropTypes.bool,
  onEdit: PropTypes.func,
  onView: PropTypes.func,
  handleCheckboxChange: PropTypes.func
};

export default forwardRef(ClientList);
